// ボタン表記の一元管理。「最後に入力があったデバイスの表記を出す」= 業界標準方式
//   (Unity InputSystem / UE CommonUI / Steam Input が同方式)。
//   パッドのグリフは Gamepad.id の Vendor ID で判別: Sony=054c → PS 表記、それ以外 → Xbox 表記。
//   出所: docs/candidates/2026-07-24-controller-full-support-ticket.md P4

import { cloneBindings, DEFAULT_KEY_BINDINGS, kbmCodeLabel, padButtonLabel, type KeyBindings } from './key_bindings'

export type LabelKey = 'attack' | 'parry' | 'step' | 'retry' | 'pause' | 'move'

export type InputDeviceView = { device: 'kbm' | 'pad', padGlyph: 'ps' | 'xbox' }

const LABELS: Record<'kbm' | 'ps' | 'xbox', Record<LabelKey, string>> = {
  kbm:  { attack: '左クリック', parry: 'Space', step: '右クリック', retry: 'R',  pause: 'Esc',     move: 'A / D' },
  ps:   { attack: 'R1',        parry: 'L1',    step: '○',         retry: '✕', pause: 'OPTIONS', move: '左スティック' },
  xbox: { attack: 'RB',        parry: 'LB',    step: 'B',         retry: 'A',  pause: 'MENU',    move: '左スティック' },
}

// 戦闘の所作の表記は、 作者が割り当てたキーに合わせる (2026-10-31 キー設定)。 input.ts が割り当てを変えるたびに渡す。
let labelBindings: KeyBindings = cloneBindings(DEFAULT_KEY_BINDINGS)
export function setLabelBindings(b: KeyBindings): void { labelBindings = cloneBindings(b) }

export function inputLabel(key: LabelKey, view: InputDeviceView): string {
  if (view.device === 'kbm') {
    const k = labelBindings.kbm
    if (key === 'attack' || key === 'parry' || key === 'step') return kbmCodeLabel(k[key])
    if (key === 'move') return `${kbmCodeLabel(k.left)} / ${kbmCodeLabel(k.right)}`
    return LABELS.kbm[key]
  }
  if (key === 'attack' || key === 'parry' || key === 'step') return padButtonLabel(labelBindings.pad[key], view.padGlyph)
  return LABELS[view.padGlyph][key]
}

// テキスト中の {attack} {parry} {step} {retry} {pause} {move} トークンを現デバイスの表記へ置換。
//   表記が仮名・漢字で終わる時 (左クリック・左スティック) は、後ろの空白を詰める
//   (「左クリック を続けて押し」の浮いた空白・10-28 初見の試遊の指摘)。Space・R1 などの英字の後ろは空けたまま。
const TOKEN_RE = /\{(attack|parry|step|retry|pause|move)\}( ?)/g
const KANA_KANJI_END = /[぀-ヿ一-鿿]$/
export function resolveInputText(text: string, view: InputDeviceView): string {
  return text.replace(TOKEN_RE, (_match, key: string, sp: string) => {
    const label = inputLabel(key as LabelKey, view)
    return KANA_KANJI_END.test(label) ? label : label + sp
  })
}
